import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Icon,
  useTheme,
} from "@mui/material";
import moment from "moment";
import FetchHook from "../../Api/FetchHooks";
import PageLoader from "../../Components/PageLoader";

// Import all the icons
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import PersonAddDisabledOutlinedIcon from "@mui/icons-material/PersonAddDisabledOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import AdminStatCard from "./Components/AdminStatCard";
import Page from "../../Components/Page";
import { useNavigate } from "react-router-dom";
import DateRangePicker from "../../Components/DateRangePicker";

// --- CARD CONFIGURATION ---
const cardConfig = {
  total_members: {
    title: "All Members",
    icon: PeopleAltOutlinedIcon,
    color: "success",
    path: "/users/customers",
  },
  fully_registered: {
    title: "Fully Registered",
    icon: PersonOutlineOutlinedIcon,
    color: "primary",
    path: "/users/customers?status=fully_registered",
  },
  pending_members: {
    title: "Pending Members",
    icon: PersonAddAltOutlinedIcon,
    color: "info",
    path: "/users/customers?status=pending",
  },
  blocked_members: {
    title: "Blocked Members",
    icon: PersonOffOutlinedIcon,
    color: "warning",
    path: "/users/customers?status=blocked",
  },
  exit_members: {
    title: "Total Member Exits",
    icon: PersonRemoveOutlinedIcon,
    color: "error",
    path: "/users/customers?status=exited",
  },
  suspended_members: {
    title: "Suspended Members",
    icon: PersonAddDisabledOutlinedIcon,
    color: "secondary",
    path: "/users/customers?status=suspended",
  },
};

const CCRDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [startDate, setStartDate] = useState(new Date("2025-01-01"));
  const [endDate, setEndDate] = useState(new Date());

  const [loading, response, request] = FetchHook();

  useEffect(() => {
    handleSearch();
  }, []);

  const handleSearch = async () => {
    const start = moment(startDate).format("YYYY-MM-DD");
    const end = moment(endDate).format("YYYY-MM-DD");
    await request({
      url: `/dashreport?start_date='${start}'&end_date='${end}'`,
    });
  };

  return (
    <Page title={"CCR Dashboard"}>
      {loading ? (
        <PageLoader />
      ) : response?.data?.Dashboard ? (
        <Grid container spacing={3}>
          {/* Stat Cards */}
          <Grid item xs={12} md={12}>
            <Card>
              <CardContent>
                <Box sx={{ my: 2 }}>
                  <DateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                    onSearch={handleSearch}
                    loading={loading}
                  />
                </Box>

                <Grid container spacing={2}>
                  {response.data.Dashboard.map((item) => {
                    const config = cardConfig[item.label];
                    if (!config) return null;

                    const totalMembers =
                      response.data.Dashboard.find(
                        (d) => d.label === "total_members"
                      )?.count || 1;

                    return (
                      <Grid item key={item.label} xs={12} sm={6} md={6} lg={3}>
                        <AdminStatCard
                          title={config.title}
                          count={item.count}
                          icon={config.icon}
                          color={config.color}
                          percentage={(item.count / totalMembers) * 100}
                          // onClick={() => navigate(config.path)}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Box
          sx={{
            mt: 10,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Icon sx={{ fontSize: 80, color: "grey.400" }}>
            <CancelOutlinedIcon fontSize="inherit" />
          </Icon>
          <Typography variant="h6" color="text.secondary">
            {response?.response?.data?.StatusMessage || "Could not load data."}
          </Typography>
        </Box>
      )}
    </Page>
  );
};

export default CCRDashboard;
